import React from 'react';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faFileImage,
  faFileLines,
  faFilePdf,
  faFileWord,
} from '@fortawesome/free-solid-svg-icons';

import type {DocumentUploadFile} from './documentDefinitions';

const fileImageIcon = faFileImage as IconProp;
const fileLinesIcon = faFileLines as IconProp;
const filePdfIcon = faFilePdf as IconProp;
const fileWordIcon = faFileWord as IconProp;

type DocumentFileRowProps = {
  file: DocumentUploadFile;
  onDownload?: (file: DocumentUploadFile) => void;
  onRemove?: (file: DocumentUploadFile) => void;
};

function prettyBytes(size?: number) {
  if (!size) return '';

  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let index = 0;
  let current = size;

  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }

  return `${current.toFixed(1)} ${units[index]}`;
}

function getFileExtension(file: DocumentUploadFile) {
  return String(file.extension || file.name?.split('.').pop() || '')
    .trim()
    .toLowerCase();
}

function getFileIcon(file: DocumentUploadFile) {
  const extension = getFileExtension(file);

  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension)) {
    return fileImageIcon;
  }

  if (extension === 'pdf') return filePdfIcon;
  if (['doc', 'docx'].includes(extension)) return fileWordIcon;

  return fileLinesIcon;
}

const DocumentFileRow: React.FC<DocumentFileRowProps> = ({
  file,
  onDownload,
  onRemove,
}) => {
  const isUploading = file.status === 'uploading';
  const isFailed = file.status === 'failed';
  const extension = getFileExtension(file);

  return (
    <div
      className={`document-file-row${isUploading ? ' is-uploading' : ''}${
        isFailed ? ' is-failed' : ''
      }`}
    >
      <div className="document-file-row__meta">
        <div
          className={`document-file-row__icon document-file-row__icon--${
            extension || 'file'
          }`}
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={getFileIcon(file)} />
        </div>

        <div className="document-file-row__text">
          <div className="document-file-row__name">{file.name}</div>
          <div className="document-file-row__sub">
            {file.uploadedAt ? (
              <span>{new Date(file.uploadedAt).toLocaleString()}</span>
            ) : null}
            {file.size ? <span> • {prettyBytes(file.size)}</span> : null}
            {isUploading ? (
              <span className="document-file-row__progress">
                {' '}
                • {Math.floor(file.progress)}%
              </span>
            ) : null}
            {isFailed && file.error ? (
              <span className="document-file-row__error"> • {file.error}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="document-file-row__actions">
        {file.url ? (
          <button
            type="button"
            className="link-button"
            onClick={() => onDownload?.(file)}
            aria-label={`Download ${file.name}`}
          >
            Download
          </button>
        ) : null}

        <button
          type="button"
          className="link-button document-file-row__remove-btn"
          onClick={() => onRemove?.(file)}
          aria-label={`Remove ${file.name}`}
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default DocumentFileRow;
